export const CCT_BUSCAR_DADOS_MODULE_NAME = 'cct_buscar_dados'

export const CCT_BUSCAR_DADOS_PARAM_DIRETORIO = 'diretorio'
export const CCT_BUSCAR_DADOS_PARAM_WEBHOOK = 'webhook_n8n'

export interface CctBuscarDadosParameter {
  id: string
  key: string
  value: string
  description?: string | null
  created_at: string
  updated_at: string
}

export type CctBuscarDadosResultadoStatus =
  | 'sucesso'
  | 'erro'
  | 'pasta_nao_encontrada'
  | 'sem_zip'

export interface CctBuscarDadosSalario {
  valor: string
  vigencia: string
  descricao: string
}

export interface CctBuscarDadosProfissaoItem {
  profissao: string
  salarios: CctBuscarDadosSalario[]
}

export interface CctBuscarDadosResultado {
  id: string
  id_scgp: number
  arquivo_nome: string | null
  convencao_ids: number[] | null
  status: CctBuscarDadosResultadoStatus | string
  resultado: CctBuscarDadosProfissaoItem[] | Record<string, unknown> | null
  erro: string | null
  created_by: string | null
  created_at: string
}

export interface CctBuscarDadosCategoria {
  id: number
  nome: string | null
}

export interface CctBuscarDadosConvencao {
  id: number
  created_at: string
  cnpj_entidade_laboral: string | null
  cnpj_entidade_patronal: string | null
  id_scgp: number | null
  status: string | null
  status_mte: string | null
  cod_categoria: string | null
  desc_territorial: string | null
  num_vidas: number | null
  convenios_qt: number | null
  data_inicio_vigencia: string | null
  data_fim_vigencia: string | null
}

export interface CctBuscarDadosFilters {
  idScgps?: string[]
  cnpjsLaborais?: string[]
  cnpjsPatronais?: string[]
  codCategorias?: string[]
}

export type CctBuscarDadosProcessItemStatus =
  | 'aguardando'
  | 'lendo_pasta'
  | 'enviando'
  | 'sucesso'
  | 'pasta_nao_encontrada'
  | 'sem_zip'
  | 'erro'
  | 'cancelado'

export interface CctBuscarDadosProcessItem {
  idScgp: number
  convencaoIds: number[]
  status: CctBuscarDadosProcessItemStatus
  arquivoNome?: string | null
  message?: string | null
}

export interface CctBuscarDadosWebhookPayload {
  /** Documento já extraído do zip (docx/pdf/html). */
  file: File | Blob
  fileName: string
  idScgp: number
}

function asString(value: unknown): string {
  if (typeof value === 'string') return value
  if (value == null) return ''
  return String(value)
}

function parseSalario(raw: unknown): CctBuscarDadosSalario | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  return {
    valor: asString(obj.valor),
    vigencia: asString(obj.vigencia),
    descricao: asString(obj.descricao),
  }
}

function parseProfissaoItem(raw: unknown): CctBuscarDadosProfissaoItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const salariosRaw = obj.salarios
  const salarios = Array.isArray(salariosRaw)
    ? salariosRaw.map(parseSalario).filter((s): s is CctBuscarDadosSalario => s != null)
    : []
  return {
    profissao: asString(obj.profissao),
    salarios,
  }
}

/**
 * Normaliza o campo resultado do n8n para a lista de profissões/salários.
 * Aceita array direto ou wrappers comuns ({ data }, { resultado }, etc.).
 */
export function parseCctBuscarDadosProfissoes(
  resultado: CctBuscarDadosResultado['resultado'],
): CctBuscarDadosProfissaoItem[] {
  if (resultado == null) return []

  if (Array.isArray(resultado)) {
    return resultado
      .map(parseProfissaoItem)
      .filter((item): item is CctBuscarDadosProfissaoItem => item != null)
  }

  if (typeof resultado === 'object') {
    const obj = resultado as Record<string, unknown>
    for (const key of ['data', 'resultado', 'items', 'profissoes']) {
      const nested = obj[key]
      if (Array.isArray(nested)) {
        return nested
          .map(parseProfissaoItem)
          .filter((item): item is CctBuscarDadosProfissaoItem => item != null)
      }
    }
  }

  return []
}

export function isCctBuscarDadosStatusSucesso(status: string | null | undefined): boolean {
  const s = (status || '').trim().toLowerCase()
  return s === 'sucesso' || s === 'success' || s === 'ok'
}
